
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useManualRefHistory } from "@vueuse/core";
import { v4 as uuidv4 } from "uuid";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import DesignerHeader from "@/views/formBuilder/components/DesignerHeader.vue";
import EmptyContent from "./components/EmptyContent.vue";
import DrawComponent from "./components/DrawComponent.vue";
import VZDropMessage from "./components/DropMessage.vue";
import DropComponentModal from "./components/DropComponentModal.vue";
import OverrideTableModal from "./components/OverrideTableModal.vue";
import { Field, Form } from "vee-validate";
import {
  droppableControls,
  ISaveFormServer,
  DroppableComponent,
  IDroppableComponent,
  isValidJson,
  IContextMenu,
  TSIDEBAROPTIONS,
  transformRowData,
  ISectionRow,
  ISectionColumn,
  Component,
  getUniqueKey,
  transformToTableRow,
} from "./components/dynamicValidation";
import { SectionDesignAPI } from "@/services/api/SectionDesign.service";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import { getLookups } from "@/services/api/Lookup.service";
import { IOptionModel } from "@/interfaces/iPageModel";
import debounce from "lodash.debounce";
import MobilePreviewModal from "@/views/formBuilder/modal/MobilePreviewModal.vue";
import TabletPreviewModal from "@/views/formBuilder/modal/TabletPreviewModal.vue";
import TemplatePreviewModal from "@/views/formBuilder/modal/TemplatePreviewModal.vue";
import { IFormSections } from "@/interfaces/iForm";
import FormSectionModal from "@/views/form/modal/SectionDetailModal.vue";
import { Actions } from "@/store/enums/StoreEnums";
import store from "@/store";
export interface SelectedDropElement {
  type: DroppableComponent;
  parentId: string;
}
export default defineComponent({
  name: "form-builder",
  components: {
    DesignerHeader,
    EmptyContent,
    VZDropMessage,
    DrawComponent,
    DropComponentModal,
    Form,
    Field,
    MobilePreviewModal,
    TabletPreviewModal,
    TemplatePreviewModal,
    FormSectionModal,
    OverrideTableModal,
  },
  setup() {
    const route = useRoute();

    const selectedSection = ref<IFormSections>();
    const toast = useToast();
    const selectedTD = ref<string>("");
    const sectionList = ref<any[]>();
    const data = { row: 1, col: 1 } as any;
    const mobilePreviewData = ref<any>([]);
    const tabletPreviewData = ref<any>([]);
    const controls = [];
    const selectedControl = ref<string[]>([]);
    const vzAttributes = ref({}) as any;
    const menu = ref<any>();
    const showDetailDialog = ref<any>(false);
    const buttonClicked = ref<any>(false);
    const showInitialDialog = ref<any>(false);
    const formStructure = ref<Array<ISectionRow>>([]);
    const selectedColumns = ref<string[]>([]);
    const selectedDifferentRowCols = ref<string[]>([]);
    const selectControl = ref("");
    const selectControlColumn = ref("");
    let noOfColumns = ref(0);
    const selectedDropElement = ref<SelectedDropElement>();
    const formSection = ref<IFormSections>();
    const searchTermDropableComponent = ref("");
    const searchTermTemplate = ref("");
    const templateOptions = ref<{ label: string; value: string }[]>([]);
    const filteredTemplateOptions = ref<{ label: string; value: string }[]>([]);
    const MAX_COLUMNS = 6;
    const MAX_BLOCKS = 60;
    const selectedRows = ref([] as any);
    const isTemplate = ref(false);
    const options = ref<IOptionModel[]>([{ text: "", value: "" }]);
    const sideBarOption = ref<TSIDEBAROPTIONS>("CONTROLS");
    const webIdUUID = ref(new Map<string, number | string>());
    const isMounted = ref(false);
    const inProgressWebId = ref(0);
    const debouncedWatch = ref();
    const masks = ref<any>();
    const loading = ref(false);
    const showModal = ref(false);
    const showOverrideTableModal = ref(false);
    const selectedSectionId = ref();
    const selectedTemplateId = ref();
    const selectedTemplateName = ref();
    const DEBOUNCE_TIME = 1000;
    const selectedItemsForCalculations = ref<any[]>([]);
    const scrollRef = ref<HTMLElement | null>(null);
    const scrollControl = ref<HTMLElement | null>(null);

    const STYLES = [
      "padding",
      "defaultFonts",
      "fontWeight",
      "borderWidth",
      "borderStyle",
      "defaultBorder",
      "fontSize",
      "margin",
      "borderColor",
      "borderSize",
      "backgroundColor",
      "fontStyle",
      "textDecoration",
      "color",
      "fontFamily",
    ];
    const VZ_CONTROL_CLASS = ["VZ_TABLE_INPUT", "VZ_TABLE_BUTTON"];
    // https://vueuse.org/core/usemanualrefhistory/
    const { history, commit, undo, redo, canUndo, canRedo, clear } =
      useManualRefHistory(formStructure, {
        clone: true,
      });
    const filterdDroppableControls = ref<Array<IDroppableComponent>>([]);
    const columnIds = computed(() => {
      const ids = formStructure.value
        .flatMap((row) => row.columns)
        .filter((col) => col?.attrs?.id)
        .map((col) => col?.attrs?.id);
      return ids;
    });

    const openDetailModal = async (formSection) => {
      selectedTemplateId.value = formSection.value;
      selectedTemplateName.value = formSection.label;
    };

    const getPreviewData = async (device) => {
      if (device == "mobile") {
        await getMobilePreviewData();
      } else {
        await getTabletPreviewData();
      }
    };

    const applyOperator = () => {
      const operator = vzAttributes.value["operator"];
      const textarea = document.getElementById("operators-area") as HTMLElement;
      if (textarea !== null)
        textarea.innerHTML += " " + selectControl.value + " " + operator;
    };

    const spanInColumn = computed(() => {
      return formStructure?.value?.map((row) =>
        row?.columns?.reduce((pre, curr) => +curr?.colSpan + +pre, 0)
      );
    });
    const maxSpanInColumn = computed(() => {
      return Math.max(...spanInColumn.value);
    });
    const getSelectedComponent = computed(() => {
      return formStructure.value
        .flatMap((row) => row.columns.flatMap((col) => col.components))
        .find((item) => item?.attrs?.id === selectControl.value);
    });
    const sectionId = computed(() => {
      return route.params.id as string;
    });
    const isRowSavedOnServer = computed(() => {
      return formStructure.value.flatMap((row) => {
        return row?.webId ? true : false;
      });
    });

    const toggleAttr = (attr, label) => {
      if (label === "cameraOnly") {
        vzAttributes._value.cameraOnly = attr;
      }
      if (label === "readonly") {
        vzAttributes._value.readonly = attr;
        if (attr === true) {
          vzAttributes._value.required = false;
        }
      }
      if (label === "required") {
        vzAttributes._value.required = attr;
        if (attr === true) {
          vzAttributes._value.readonly = false;
        }
      }
      applyAttributes();
    };

    const isChecked = (attrs, attrsVal) => {
      return vzAttributes.value[attrs] == attrsVal;
    };

    const getMobilePreviewData = async (formId?, siteId?) => {
      return new Promise((resolve, reject) => {
        mobilePreviewData.value = [];
        SectionDesignAPI.getInstance()
          .getFormDesignPreview(sectionId.value, "MOBILE", formId, siteId)
          .then((res) => {
            mobilePreviewData.value = res;
            resolve(true);
          })
          .catch((err) => {
            reject(err);
          });
      });
    };

    const getTabletPreviewData = async (formId?, siteId?) => {
      return new Promise((resolve, reject) => {
        tabletPreviewData.value = [];
        SectionDesignAPI.getInstance()
          .getFormDesignPreview(sectionId.value, "TABLET", formId, siteId)
          .then((res) => {
            tabletPreviewData.value = res;
            resolve(true);
          })
          .catch((err) => {
            reject(err);
          });
      });
    };

    const updatePreviewData = async (data) => {
      if (data.device == "tablet") {
        await getTabletPreviewData(data.form, data.site)
          .then((res) => {
            toast.success(`Changes applied`);
          })
          .catch((err) => {
            console.error(err);
          });
      } else {
        await getMobilePreviewData(data.form, data.site)
          .then((res) => {
            toast.success(`Changes applied`);
          })
          .catch((err) => {
            console.error(err);
          });
      }
    };

    const selectTextLink = () => {
      //tab-pane fade show active
      const textTabLink = document.getElementById(
        "text-tab-link"
      ) as HTMLElement;
      if (textTabLink != null)
        textTabLink.className = "nav-link text-gray-600 active";
      const textTab = document.getElementById("text-tab") as HTMLElement;
      if (textTab != null) textTab.className = "tab-pane fade show active";

      const appearanceTabLink = document.getElementById(
        "appearance-tab-link"
      ) as HTMLElement;
      if (appearanceTabLink != null)
        appearanceTabLink.className = "nav-link text-gray-600";
      const appearanceTab = document.getElementById(
        "appearance-tab"
      ) as HTMLElement;
      if (appearanceTab != null) appearanceTab.className = "tab-pane fade";

      const propertiesTabLink = document.getElementById(
        "properties-tab-link"
      ) as HTMLElement;
      if (propertiesTabLink != null)
        propertiesTabLink.className = "nav-link text-gray-600";
      const propertiesTab = document.getElementById(
        "properties-tab"
      ) as HTMLElement;
      if (propertiesTab != null) propertiesTab.className = "tab-pane fade";
    };

    onMounted(async () => {
      setCurrentPageBreadcrumbs("Form Builder", ["Home", "Form Builder"]);
      // store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading");
      //get section data
      const elements = document.querySelectorAll("#menu-section-3");
      elements.forEach((el) => {
        const a = el.querySelector("a");
        if (a) {
          const href = a.getAttribute("href");
          if (href && href.startsWith("#")) {
            const routePath = href.substring(1);
            if (routePath === "/sections") {
              a.classList.add("router-link-active", "router-link-exact-active");
            }
          }
        }
      });

      data.initial = true;
      let flag = false;
      document.addEventListener("keydown", function onPress(event) {
        if (event.key == "Delete") {
          deleteSelectedItem();
          flag = true;
        }
      });
      debouncedWatch.value = debounce(async (newValue) => {
        if (!flag) {
          if (isMounted.value && (canUndo.value || canRedo.value)) {
            const serverData = await transformToServer();
            if (inProgressWebId.value > 0) {
              await SectionDesignAPI.getInstance()
                .patchFormInprocess(inProgressWebId.value, serverData)
                .then(() => {
                  if (buttonClicked.value) toast.success(`Changes applied`);
                  buttonClicked.value = false;
                })
                .catch((err) => {
                  console.error(err);
                });
            } else {
              const data =
                await SectionDesignAPI.getInstance().postFormInprocess(
                  sectionId.value,
                  serverData
                );
              inProgressWebId.value = data?.webId ?? 0;
            }
          }
        }
      }, DEBOUNCE_TIME);
      initTable();
      const sectionFormURL = "forms/filter/FORM_SECTION_TEMPLATE";
      const responses = await Promise.all([
        getLookups(
          "static-data/filter/LOOKUP_FILTER/filters?filter[lookupName]=LKMask"
        ),
        getLookups(sectionFormURL),
        SectionDesignAPI.getInstance().getSectionDetail(sectionId.value),
      ]);
      masks.value = responses[0];
      sectionList.value = responses[1];
      const sectionForms = responses[1]?.map((item) => {
        return {
          label: item.text,
          value: item.value,
        };
      });
      templateOptions.value = sectionForms;
      getFilteredSection();
      getFilteredControl();
      isTemplate.value = responses[2].template;
      formSection.value = responses[2];
    });
    onBeforeUnmount(() => {
      debouncedWatch?.value?.cancel();
      SectionDesignAPI.cancelToken = null;
      selectTextLink();
    });
    const dragElement = (ev) => {
      ev.dataTransfer.setData("text", ev.target.id);
    };
    const lastColumnColSpan = (colspan: number, index: number) => {
      const col = spanInColumn?.value[index];
      if (col < maxSpanInColumn.value) {
        colspan += maxSpanInColumn.value - col;
      } else if (col > maxSpanInColumn.value) {
        colspan += -maxSpanInColumn.value + col;
      }
      return +colspan;
    };
    const confirmDiscard = async () => {
      try {
        if (inProgressWebId.value > 0) {
          await SectionDesignAPI.getInstance().deleteFormInprocess(
            inProgressWebId.value
          );
          nextTick(() => {
            isMounted.value = false;
            initTable();
          });
        }
      } catch (e) {
        console.log(e);
      }
    };
    const initTable = async () => {
      try {
        store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading");
        loading.value = true;
        clear();
        const values = await Promise.all([
          SectionDesignAPI.getInstance().getFormDesign(sectionId.value),
          SectionDesignAPI.getInstance().getFormInprocess(sectionId.value),
        ]);
        let data = values[0];
        formStructure.value = [];
        clear();
        if (values[1]) {
          inProgressWebId.value = values[1].webId;
          data = values[1].data.formSectionRowModels;
        } else {
          inProgressWebId.value = 0;
        }
        data.forEach((rowElement) => {
          if (!rowElement.deleted) {
            const row = {
              rowProp: {},
              columns: [] as ISectionColumn[],
              webId: rowElement.webId,
              key: rowElement?.key ?? uuidv4(),
              formSectionId: rowElement?.formSectionId ?? sectionId.value,
              rowOrder: rowElement?.rowOrder ?? 1,
              rowSpan: rowElement?.rowSpan ?? 1,
            };
            setWebIDUUID({ key: row.key, webId: row.webId });
            if (rowElement.columns) {
              rowElement.columns.forEach((colElement) => {
                if (!colElement.deleted) {
                  const col = {
                    attrs: {},
                    colSpan: +colElement.colSpan,
                    rowSpan: colElement.rowSpan,
                    components: [] as Component[],
                    webId: colElement.webId,
                    formSectionRowId: rowElement.webId,
                    class: colElement?.cssClass ?? "",
                    key: colElement?.key ?? uuidv4(),
                  };
                  setWebIDUUID({ key: col.key, webId: col.webId });
                  if (colElement.attrs) {
                    colElement.attrs.forEach((attr) => {
                      col.attrs[attr.key] = attr.value;
                    });
                  }
                  if (colElement.components) {
                    colElement.components.forEach((comp) => {
                      if (!comp.deleted) {
                        const component = {
                          attrs: {
                            label: comp.name,
                          },
                          webId: comp.webId,
                          formSectionRowColumnId: colElement.webId,
                          component: comp.component,
                          name: comp.name,
                          key: comp?.key ?? uuidv4(),
                          direction: comp?.direction,
                          linkedLabelKey: comp?.linkedLabelKey ?? "",
                          controlId: comp?.controlId,
                        };
                        setWebIDUUID({
                          key: component.key,
                          webId: `${component.webId}`,
                        });
                        if (comp.attrs) {
                          comp.attrs.forEach((attr) => {
                            if (isValidJson(attr.value)) {
                              component.attrs[attr.key] = JSON.parse(
                                attr.value
                              );
                            } else {
                              component.attrs[attr.key] = attr.value;
                            }
                          });
                        }
                        col.components.push(component);
                      }
                    });
                  }
                  row.columns.push(col);
                }
              });
              formStructure.value.push(row);
            }
          }
        });
        nextTick(() => {
          isMounted.value = true;
        });
      } catch (e) {
        console.log(e);
      } finally {
        loading.value = false;
        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");
      }
    };

    const findColumnIDFromPath = (path: HTMLElement[]) => {
      return (
        path?.find(
          (tag) =>
            tag.tagName === "TD" && tag.id && columnIds.value?.includes(tag.id)
        )?.id ?? ""
      );
    };
    const findControlIDFromPath = (path: HTMLElement[]) => {
      return (
        path?.find((tag) =>
          VZ_CONTROL_CLASS.some((vzClass) => tag?.classList?.contains(vzClass))
        )?.id ?? ""
      );
    };
    const findRowFromSelectedColumnId = (id: string) => {
      return (
        formStructure.value?.findIndex((row) =>
          row.columns?.find((item) => item?.attrs?.id === id)
        ) ?? -1
      );
    };
    const findColumnFromSelectedColumnId = (id: string) => {
      return (
        formStructure?.value[
          findRowFromSelectedColumnId(id)
        ]?.columns.findIndex((item) => item?.attrs?.id === id) ?? -1
      );
    };
    const drop = (ev, ui) => {
      if (ev.stopPropagation) {
        ev.stopPropagation(); // stops the browser from redirecting.
      }
      const ele = ev.dataTransfer.getData("text");
      const selectedDropped = droppableControls.value.find(
        (item) => item.id === ele || ele.indexOf(item.id) > -1
      );
      if (!selectedDropped) {
        return;
      }
      let dropCol = ev.target;
      while (dropCol.tagName != "TD") {
        dropCol = dropCol.parentNode;
      }
      selectedDropElement.value = {
        type: selectedDropped?.type ?? DroppableComponent.BUTTON,
        parentId: dropCol.id,
      };
      // now open dialog
      // const modal = new Modal(
      //   document.getElementById("vz_drop_component_props") as HTMLElement
      // );
      // modal.show();
      showModal.value = true;
    };

    const deleteSelectedItem = () => {
      const rowIndex = findRowFromSelectedColumnId(selectControlColumn.value);
      const columnIndex = findColumnFromSelectedColumnId(
        selectControlColumn.value
      );
      if (rowIndex > -1 && columnIndex > -1) {
        const elementIndex = formStructure.value[rowIndex].columns[
          columnIndex
        ]?.components?.findIndex(
          (component) => component?.attrs?.id === selectControl.value
        );
        if (elementIndex > -1) {
          unLinkedItems(selectControl.value);
          formStructure.value[rowIndex].columns[columnIndex]?.components.splice(
            elementIndex,
            1
          );
          commit();
        }
      }
    };
    const unLinkedItems = (key: string) => {
      formStructure.value.forEach((row) => {
        row.columns.forEach((col) => {
          col.components.forEach((comp) => {
            if (comp.linkedLabelKey === key) {
              comp.linkedLabelKey = "";
            }
          });
        });
      });
    };
    const allowDrop = (ev) => {
      ev.preventDefault();
    };

    const showContextMenu = (event) => {
      const path = event.path || (event.composedPath && event.composedPath());
      selectedTD.value = findColumnIDFromPath(path);
      data.selectedItem = event.srcElement;
      menu.value.show(event);
    };

    const duplicateElement = () => {
      const rowIndex = findRowFromSelectedColumnId(selectControlColumn.value);
      const columnIndex = findColumnFromSelectedColumnId(
        selectControlColumn.value
      );
      if (rowIndex > -1 && columnIndex > -1) {
        const elementIndex = formStructure.value[rowIndex].columns[
          columnIndex
        ]?.components?.findIndex(
          (component) => component?.attrs?.id === selectControl.value
        );
        if (elementIndex > -1) {
          const duplicacte = formStructure.value[rowIndex].columns[columnIndex]
            ?.components[elementIndex] as Component;
          if (duplicacte) {
            const controlId = `${duplicacte.component}_${
              sectionId.value
            }_${getUniqueKey()}`;
            const key = uuidv4();
            const dComponent = {
              ...duplicacte,
              controlId,
              name: duplicacte.name + " Copy",
              key,
              webId: null,
              attrs: {
                ...duplicacte.attrs,
                id: key,
                label: duplicacte?.attrs?.label + " Copy",
              },
            };
            formStructure.value[rowIndex].columns[columnIndex]?.components.push(
              dComponent
            );
            commit();
          }
        }
      }
    };

    const selectColumn = (event) => {
      const path = event.path || (event.composedPath && event.composedPath());
      if (event.shiftKey) {
        event.preventDefault();
        const columnUUID = findColumnIDFromPath(path);
        const rowIndex = findRowFromSelectedColumnId(columnUUID);
        if (columnUUID) {
          if (selectedColumns.value.length > 0) {
            //Check if selected columns are within same column
            if (
              selectedColumns.value.some(
                (item) => findRowFromSelectedColumnId(item) !== rowIndex
              )
            ) {
              selectedColumns.value = [];
            }
          }

          if (
            selectedColumns.value.findIndex((item) => item === columnUUID) ===
            -1
          ) {
            selectedColumns.value = [...selectedColumns.value, columnUUID];
          } else {
            selectedColumns.value = [columnUUID];
          }

          if (selectedColumns.value.length > 1) {
            const indexes = selectedColumns.value
              .map((item) => findColumnFromSelectedColumnId(item))
              ?.filter((index) => index > -1);
            if (indexes.length > 1) {
              for (
                let i = Math.min(...indexes);
                i <= Math.max(...indexes);
                i++
              ) {
                const id = formStructure.value[rowIndex].columns[i]?.key ?? "";
                if (
                  id &&
                  selectedColumns.value.findIndex((item) => item === id) === -1
                ) {
                  selectedColumns.value = [...selectedColumns.value, id];
                }
              }
            }
          }
        }
      } else if (event.ctrlKey) {
        let selectedCol = event.srcElement;
        while (selectedCol.tagName != "TD") {
          selectedCol = selectedCol.parentNode;
        }
        selectedColumns.value.push(selectedCol.id);
        selectedColumns.value.map((id) => {
          let elem = document.getElementById(id) as HTMLElement;
          elem.style.backgroundColor = "#EAF9B6";
        });
      } else {
        if (event.button == 2) {
          // Do nothing, its right click for context menu
        } else {
          unselectRows();
          selectControl.value = findControlIDFromPath(path);
          selectControlColumn.value = findColumnIDFromPath(path);
        }
      }
    };
    const menuItem = ref<Array<IContextMenu>>([]);
    //// ROWS START
    const addRowAbove = () => {
      const rowIndex = findRowFromSelectedColumnId(selectedTD.value);
      const rowUUID = uuidv4();
      const columnsInfo = formStructure?.value[rowIndex].columns.map((col) => {
        const key = uuidv4();
        return {
          colSpan: +col?.colSpan || 1,
          // rowSpan: +col?.rowSpan || 1,
          webId: null,
          formSectionId: sectionId.value,
          key,
          attrs: {
            id: key,
          },
          components: [],
        };
      });
      if (isRowSavedOnServer?.value[rowIndex]) {
        formStructure.value[rowIndex].dirty = true;
      }
      const row = {
        columns: [...columnsInfo],
        key: rowUUID,
        rowOrder: rowIndex + 1,
        rowSpan: 1,
        formSectionId: sectionId.value,
      };
      formStructure?.value.splice(rowIndex, 0, row);
      commit();
    };
    const addRowBelow = () => {
      const rowIndex = findRowFromSelectedColumnId(selectedTD.value);
      const rowUUID = uuidv4();
      const columnsInfo = formStructure?.value[rowIndex].columns.map((col) => {
        const key = uuidv4();
        return {
          colSpan: +col?.colSpan || 1,
          webId: null,
          key,
          attrs: {
            id: key,
          },
          components: [],
        };
      });
      if (isRowSavedOnServer?.value[rowIndex]) {
        formStructure.value[rowIndex].dirty = true;
      }
      const row = {
        columns: [...columnsInfo],
        key: rowUUID,
        rowOrder: rowIndex + 2,
        rowSpan: 1,
        formSectionId: sectionId.value,
        formSectionRowColumnModels: [],
      };
      formStructure?.value.splice(rowIndex + 1, 0, row);
      commit();
    };
    const deleteRow = async () => {
      const rowIndex = findRowFromSelectedColumnId(selectedTD.value);
      if (formStructure?.value.length > 0) {
        if (rowIndex > -1) {
          formStructure.value[rowIndex].columns.forEach((col) => {
            col.components.forEach((comp) => {
              unLinkedItems(comp.attrs?.id);
            });
          });
        }
        formStructure?.value.splice(rowIndex, 1);
        commit();
      }
    };
    const selectRow = () => {
      const rowIndex = findRowFromSelectedColumnId(selectedTD.value);
      if (formStructure?.value.length > 0 && selectedRows.value.length < 2) {
        selectedRows.value.push(formStructure?.value[rowIndex]);
        selectedRows?.value.map((row) => {
          row.columns.map((col) => {
            let elem = document.getElementById(col?.attrs?.id) as HTMLElement;
            elem.style.backgroundColor = "#EAF9B6";
          });
        });
      } else {
        selectedRows.value = [];
        unselectRows();
      }
    };

    const mergeRows = async () => {
      const rowIndex1 = findRowFromSelectedColumnId(
        selectedRows.value[0]?.columns[0]?.attrs?.id
      );
      const rowIndex2 = findRowFromSelectedColumnId(
        selectedRows.value[1]?.columns[0]?.attrs?.id
      );
      if (
        formStructure.value[rowIndex1].columns.length ==
        formStructure.value[rowIndex2].columns.length
      ) {
        formStructure.value[rowIndex2]?.columns?.forEach((columns, index) => {
          formStructure.value[rowIndex1].columns[index].components = [
            ...formStructure.value[rowIndex1]?.columns[index]?.components,
            ...columns.components?.map((com) => {
              const duplicate = {
                ...com,
                webId: null,
                attrs: {
                  ...com.attrs,
                },
              };
              return duplicate;
            }),
          ];
        });
        commit();
        unselectRows();
        formStructure?.value.splice(rowIndex2, 1);
      } else {
        alert("Rows cannot be merged as column length is different");
      }
    };

    const unselectRows = () => {
      columnIds.value.forEach((id) => {
        const elem = document.getElementById(id);
        if (elem) {
          elem.style.backgroundColor = "transparent";
        }
      });
      selectedRows.value = [];
      selectedColumns.value = [];
      selectedDifferentRowCols.value = [];
    };

    const getEmptyRow = () => {
      const key = uuidv4();
      return {
        columns: [
          {
            colSpan: 1,
            rowSpan: 1,
            attrs: {
              id: key,
            },
            components: [],
            class: "",
            key,
          },
        ],
      };
    };
    const createTable = async ({ row, column, template }) => {
      try {
        store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading");
        showInitialDialog.value = false;
        if (template.value) {
          const data = await SectionDesignAPI.getInstance().getFormDesign(
            template.value
          );
          const formData = transformToTableRow(data, sectionId.value);
          formStructure.value = [...formData];
        } else {
          noOfColumns.value = column.value;
          for (let rowNum = 0; rowNum < row.value; rowNum++) {
            const emptyRow = getEmptyRow();
            for (let colNum = 1; colNum < column.value; colNum++) {
              const key = uuidv4();
              emptyRow.columns.push({
                colSpan: 1,
                rowSpan: 1,
                attrs: {
                  id: key,
                },
                components: [],
                class: "",
                key,
              });
            }
            formStructure.value.push({
              key: uuidv4(),
              columns: [...emptyRow.columns],
              rowOrder: 0,
              rowSpan: 0,
              formSectionId: sectionId.value,
            });
          }
        }
        commit();
      } catch (e) {
        console.log(e);
      } finally {
        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");
      }
    };
    //// ROWS END
    const addColumnLeft = () => {
      const rowIndex = findRowFromSelectedColumnId(selectedTD.value);
      if (
        formStructure?.value.length == 0 ||
        MAX_COLUMNS > formStructure?.value[rowIndex]?.columns.length
      ) {
        let columnIndex = findColumnFromSelectedColumnId(selectedTD.value);
        if (isRowSavedOnServer?.value[rowIndex]) {
          formStructure.value[rowIndex].dirty = true;
          if (formStructure.value[rowIndex].columns[columnIndex].webId) {
            formStructure.value[rowIndex].columns[columnIndex].dirty = true;
          }
        }
        columnIndex++;
        const key = uuidv4();
        formStructure?.value[rowIndex]?.columns?.splice(columnIndex, 0, {
          colSpan: 1,
          rowSpan: 1,
          key,
          attrs: {
            id: key,
          },
          components: [],
        });
        commit();
      }
    };
    const alignItems = (text = "start") => {
      const rowIndex = findRowFromSelectedColumnId(selectControlColumn.value);
      if (
        formStructure?.value.length == 0 ||
        formStructure?.value[rowIndex]?.columns.length
      ) {
        const columnIndex = findColumnFromSelectedColumnId(
          selectControlColumn.value
        );
        if (formStructure?.value[rowIndex]?.columns[columnIndex]) {
          formStructure.value[rowIndex].columns[
            columnIndex
          ].class = `justify-content-${text}`;
          if (isRowSavedOnServer?.value[rowIndex]) {
            formStructure.value[rowIndex].dirty = true;
            if (formStructure.value[rowIndex].columns[columnIndex].webId) {
              formStructure.value[rowIndex].columns[columnIndex].dirty = true;
            }
          }
          commit();
        }
      }
    };
    const selectForCalculation = () => {
      if (!selectedItemsForCalculations.value.includes(selectControl.value)) {
        selectedItemsForCalculations.value.push(selectControl.value);
      }
    };

    const applyCalculations = () => {
      const rowIndex = findRowFromSelectedColumnId(selectControlColumn.value);
      // Save previous Row
      const columnsCopy = formStructure?.value[rowIndex].columns;
      const selectedColumns = columnsCopy.find(
        (e) => e.key === selectControlColumn.value
      );
      const selectedIndexCol = columnsCopy.findIndex(
        (e) => e.key === selectControlColumn.value
      );
      let selectedIndexItem;
      if (selectedColumns) {
        selectedIndexItem = selectedColumns.components.findIndex(
          (e) => e.key === selectControl.value
        );
      }
      // Apply Operations on row
      let selectedItem =
        selectedColumns?.components.find(
          (e) => e.key === selectControl.value
        ) ?? null;
      // Making formula
      const formula = `({{${selectedItemsForCalculations.value.join(
        "}} + {{"
      )}}})`;
      if (selectedItem && selectedItem.attrs) {
        if (
          selectedItem.attrs.operation &&
          selectedItem.attrs.operation.length > 0
        ) {
          const uuidRegex =
            /\{\{[a-f\d]{8}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{12}\}\}/g;

          // Extract unique UUIDs from both strings
          const firstUuids = Array.from(
            new Set(selectedItem.attrs.operation.match(uuidRegex))
          );
          const secondUuids = Array.from(new Set(formula.match(uuidRegex)));
          const uniqueUuids = Array.from(
            new Set([...firstUuids, ...secondUuids])
          );
          // Replace duplicate UUIDs in the second string
          const uniqueFormula = uniqueUuids.join("+");
          selectedItem.attrs.operation = "(" + uniqueFormula + ")";
        } else {
          selectedItem.attrs.operation = formula;
        }

        columnsCopy[selectedIndexCol].components[selectedIndexItem] =
          selectedItem;
      }
    };
    const deleteColumn = () => {
      const rowIndex = findRowFromSelectedColumnId(selectedTD.value);
      const columnIndex = findColumnFromSelectedColumnId(selectedTD.value);
      if (rowIndex > -1 && columnIndex > -1) {
        if (
          formStructure.value[rowIndex].columns[columnIndex].components.length >
          0
        ) {
          formStructure.value[rowIndex].columns[columnIndex].components.forEach(
            (component) => {
              unLinkedItems(component.attrs?.id);
            }
          );
        }
        formStructure?.value[rowIndex]?.columns?.splice(columnIndex, 1);
        commit();
      }
    };

    const mergeColumns = async () => {
      if (selectedColumns.value.length > 1) {
        const indexes = selectedColumns.value
          .map((item) => findColumnFromSelectedColumnId(item))
          ?.filter((index) => index > -1);
        if (indexes.length > 1) {
          const minIndex = Math.min(...indexes) ?? 0;
          const maxIndex = Math.max(...indexes) ?? 0;
          const rowIndex = findRowFromSelectedColumnId(
            selectedColumns.value[minIndex]
          );
          const loopCounter = +minIndex + 1 ?? 0;
          let components = [] as Component[];
          for (let index = loopCounter; index <= maxIndex; index++) {
            const colComp = formStructure.value[rowIndex]?.columns[
              index
            ].components?.map((com) => {
              const duplicate = {
                ...com,
                webId: null,
                attrs: {
                  ...com.attrs,
                },
              };
              return duplicate;
            });
            components = [...components, ...colComp];
          }
          const colSpan = formStructure?.value[rowIndex].columns
            .filter((item) => selectedColumns.value.includes(item.key))
            .reduce((pre, curr) => +curr?.colSpan + +pre, 0);
          formStructure.value[rowIndex]?.columns.splice(
            minIndex + 1,
            indexes.length - 1
          );
          if (components.length > 0) {
            formStructure.value[rowIndex].columns[minIndex].components = [
              ...(formStructure.value[rowIndex].columns[minIndex]?.components ??
                []),
              ...components,
            ];
          }
          formStructure.value[rowIndex].columns[minIndex].colSpan = colSpan;
          unselectRows();
          commit();
        }
      }
    };

    const drag = (ev) => {
      ev.dataTransfer.setData("text", ev.target.id);
    };

    vzAttributes.value["defaultValue"] = computed(() => {
      return vzAttributes.value["text"];
    }).value;

    const applyAttributes = async (btnclicked?) => {
      // Sanitize fontSize: allow '2em', '1.5em', etc., or append 'px' for numbers
      if (vzAttributes._value.fontSize && vzAttributes._value.fontSize !== "") {
        let value = vzAttributes._value.fontSize.trim();
        // If value is a number, append px
        if (!isNaN(value) && !value.endsWith("px") && !value.endsWith("em")) {
          value = value + "px";
        }
        // Only allow valid CSS font-size values
        if (/^\d+(\.\d+)?(px|em|rem|%)?$/.test(value)) {
          vzAttributes._value.fontSize = value;
        } else {
          vzAttributes._value.fontSize = "";
        }
      }
      buttonClicked.value = applyAttributes;
      const rowIndex = findRowFromSelectedColumnId(selectControlColumn.value);
      const columnIndex = findColumnFromSelectedColumnId(
        selectControlColumn.value
      );
      if (rowIndex > -1 && columnIndex > -1) {
        const elementIndex = formStructure.value[rowIndex].columns[
          columnIndex
        ]?.components?.findIndex(
          (component) => component?.attrs?.id === selectControl.value
        );
        if (elementIndex > -1) {
          const ele = {
            dirty: false,
            attrs: {
              class: vzAttributes.value["classes"],
              maskMessage: vzAttributes.value["maskMessage"],
              label: vzAttributes.value["text"],
              defaultValue: vzAttributes.value["text"],
              id: vzAttributes.value["id"],
              style: {},
              operation: vzAttributes.value["operation"],
              points: vzAttributes.value["points"],
              readonly: vzAttributes.value["readonly"] ?? false,
              required: vzAttributes.value["required"] ?? false,
              condition:
                isNotNullOrEmpty(vzAttributes.value["type"]) ||
                isNotNullOrEmpty(vzAttributes.value["expression"]) ||
                isNotNullOrEmpty(vzAttributes.value["data"])
                  ? {
                      type: vzAttributes.value["type"],
                      expression: vzAttributes.value["expression"],
                      data: vzAttributes.value["data"],
                    }
                  : null,
              mask:
                vzAttributes.value["mask"] !== ""
                  ? vzAttributes.value["mask"]
                  : "",
            },
          };

          if (
            getSelectedComponent?.value !== undefined &&
            getSelectedComponent?.value?.component == "CAMERA"
          ) {
            ele.attrs["cameraOnly"] = vzAttributes.value["cameraOnly"] == true;
          }

          // Only set style properties if value is not empty
          STYLES.forEach((style) => {
            if (
              vzAttributes.value[style] !== undefined &&
              vzAttributes.value[style] !== ""
            ) {
              ele.attrs.style[style] = vzAttributes.value[style];
            }
          });
          let item =
            formStructure.value[rowIndex].columns[columnIndex]?.components[
              elementIndex
            ];
          if (item.webId) {
            ele.dirty = true;
          }
          if (
            ["RADIO_BUTTON", "LIST"].includes(item.component) &&
            options.value
          ) {
            ele.attrs["options"] = [...options.value];
          }
          if (["PARAGRAPH"].includes(item.component)) {
            item.defaultValue = vzAttributes.value["text"] ?? "";
          }
          item = { ...item, ...ele };
          formStructure.value[rowIndex].columns[columnIndex].components[
            elementIndex
          ] = item;
          if (isRowSavedOnServer?.value[rowIndex]) {
            formStructure.value[rowIndex].dirty = true;
          }
          commit();
        }
      }
    };

    const isNotNullOrEmpty = (obj) => {
      return obj !== "" && obj !== null && obj !== undefined;
    };
    const createElement = (component: Component[]) => {
      const rowIndex = findRowFromSelectedColumnId(
        selectedDropElement.value?.parentId ?? ""
      );
      const columnIndex = findColumnFromSelectedColumnId(
        selectedDropElement.value?.parentId ?? ""
      );
      if (rowIndex > -1 && columnIndex > -1) {
        for (let i = 0; i < component.length; i++) {
          if (component[i].component == "CHECK_BOX") {
            const comp = component[i];
            if (comp.attrs) {
              comp.attrs.label = "";
            }
          }
          formStructure.value[rowIndex].columns[columnIndex].components.push(
            component[i]
          );
        }
        if (isRowSavedOnServer?.value[rowIndex]) {
          formStructure.value[rowIndex].dirty = true;
        }
        if (formStructure.value[rowIndex].columns[columnIndex].webId) {
          formStructure.value[rowIndex].columns[columnIndex].dirty = true;
        }
        commit();
        showModal.value = false;
      }
    };
    const transformToServer = async (): Promise<ISaveFormServer[]> => {
      return new Promise((resolve) => {
        const dbData = [] as ISaveFormServer[];
        formStructure?.value?.forEach((rowElement, index) => {
          const row = transformRowData(rowElement, index);
          dbData.push(row);
        });
        resolve(dbData);
      });
    };
    const publishForm = async () => {
      try {
        loading.value = true;
        store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading");
        await SectionDesignAPI.getInstance().saveFormDesign(sectionId.value);
        if (inProgressWebId.value > 0) {
          await SectionDesignAPI.getInstance().deleteFormInprocess(
            inProgressWebId.value
          );
        }
        nextTick(() => {
          isMounted.value = false;
          initTable();
          toast.success(`Section design saved successfully`);
        });
      } catch (e) {
        //
      } finally {
        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");
        loading.value = false;
      }
    };
    const getFilteredControl = () => {
      if (!searchTermDropableComponent.value) {
        filterdDroppableControls.value = [...droppableControls.value]?.sort(
          (a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1)
        );
      }
      if (searchTermDropableComponent.value) {
        filterdDroppableControls.value = droppableControls.value
          .filter((item) =>
            item.label
              .trim()
              .toLowerCase()
              .includes(searchTermDropableComponent.value.trim().toLowerCase())
          )
          ?.sort((a, b) =>
            a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
          );
      }
    };
    const getFilteredSection = () => {
      if (!searchTermTemplate.value) {
        filteredTemplateOptions.value = [...templateOptions.value];
      }
      if (searchTermTemplate.value) {
        filteredTemplateOptions.value = templateOptions.value.filter((item) =>
          item.label
            .trim()
            .toLowerCase()
            .includes(searchTermTemplate.value.trim().toLowerCase())
        );
      }
    };
    const openControlSetting = () => {
      if (getSelectedComponent.value) {
        sideBarOption.value = "SETTINGS";
        return;
      }
      // toast.error(`Please select control`);
    };

    const onClickTemplate = (value) => {
      sideBarOption.value = value;
      nextTick(() => {
        setTimeout(() => {
          if (scrollRef.value) {
            scrollRef.value.scrollTo({
              top: 0,
              behavior: "smooth",
            });
          }
        }, 0);
      });
    };

    const onClickControl = (value) => {
      sideBarOption.value = value;
      nextTick(() => {
        setTimeout(() => {
          if (scrollControl.value) {
            console.log("scroll", scrollControl.value);
            scrollControl.value.scrollTo({
              top: 0,
              behavior: "smooth",
            });
          }
        }, 0);
      });
    };

    const toggleIsTemplate = async (isTemplate: boolean) => {
      // if (inProgressWebId.value > 0) {
      //   toast.error(`Please publish the form before changing the template`);
      //   return;
      // } // Should be able to make as template if eligible for publish
      try {
        const data = await transformToServer();
        //Post data to API
        await SectionDesignAPI.getInstance()
          .postIsTemplate(sectionId.value, data, !isTemplate)
          .then(() => {
            // toast.error(`Saved as template`);
          })
          .catch((err) => {
            console.error(err);
          });
      } catch (e) {
        console.log(e);
      }
    };
    // toggle top buttons settings like italics, bold, underline, font size, font family
    const toggleStyles = ({
      fontSize,
      fontStyle,
      fontWeight,
      textDecoration,
    }: {
      fontSize?: string;
      fontStyle?: string;
      fontWeight?: string;
      textDecoration?: string;
    }) => {
      if (!getSelectedComponent.value) {
        toast.error(`Please select control`);
        return;
      }
      if (!vzAttributes.value["style"]) {
        vzAttributes.value["style"] = {};
      }
      if (fontSize) {
        if (
          getSelectedComponent.value?.attrs?.fontSize === fontSize ||
          getSelectedComponent.value?.attrs?.style?.fontSize === fontSize
        ) {
          vzAttributes.value["fontSize"] = "";
        } else {
          vzAttributes.value["fontSize"] = fontSize;
        }
      }
      if (fontStyle) {
        if (
          getSelectedComponent.value?.attrs?.fontStyle === fontStyle ||
          getSelectedComponent.value?.attrs?.style?.fontStyle === fontStyle
        ) {
          vzAttributes.value["fontStyle"] = "";
        } else {
          vzAttributes.value["fontStyle"] = fontStyle;
        }
      }
      if (fontWeight) {
        if (
          getSelectedComponent.value?.attrs?.fontWeight === fontWeight ||
          getSelectedComponent.value?.attrs?.style?.fontWeight === fontWeight
        ) {
          vzAttributes.value["fontWeight"] = "";
        } else {
          vzAttributes.value["fontWeight"] = fontWeight;
        }
      }
      if (textDecoration) {
        if (
          getSelectedComponent.value?.attrs?.textDecoration ===
            textDecoration ||
          getSelectedComponent.value?.attrs?.style?.textDecoration ===
            textDecoration
        ) {
          vzAttributes.value["textDecoration"] = "";
        } else {
          vzAttributes.value["textDecoration"] = textDecoration;
        }
      }
      applyAttributes();
    };
    watch(
      getSelectedComponent,
      (newVal) => {
        if (
          newVal?.component == "LIST" ||
          newVal?.component == "RADIO_BUTTON"
        ) {
          options.value = newVal?.attrs?.options;
        }
        vzAttributes.value["classes"] = newVal?.attrs?.class ?? "";
        vzAttributes.value["text"] = newVal?.attrs?.label ?? "";
        vzAttributes.value["id"] = newVal?.attrs?.id ?? "";
        // vzAttributes.value["readonly"] = newVal?.attrs?.readonly ?? false;
        vzAttributes.value["type"] = newVal?.attrs?.condition?.type ?? "";
        vzAttributes.value["expression"] =
          newVal?.attrs?.condition?.expression ?? "";
        vzAttributes.value["data"] = newVal?.attrs?.condition?.data ?? "";
        vzAttributes.value["operation"] = newVal?.attrs?.operation ?? "";
        vzAttributes.value["points"] = newVal?.attrs?.points ?? "";
        // vzAttributes.value["required"] = newVal?.attrs?.required ?? false;
        vzAttributes.value["mask"] = newVal?.attrs?.mask ?? "";
        vzAttributes.value["maskMessage"] = newVal?.attrs?.maskMessage ?? "";
        vzAttributes.value["cameraOnly"] = newVal?.attrs?.cameraOnly ?? false;
        vzAttributes.value["readonly"] = newVal?.attrs?.readonly ?? false;
        vzAttributes.value["required"] = newVal?.attrs?.required ?? false;

        STYLES.forEach((style) => {
          if (newVal?.attrs?.style && newVal?.attrs?.style[style]) {
            vzAttributes.value[style] = newVal?.attrs?.style[style] ?? "";
          } else {
            vzAttributes.value[style] = "";
          }
        });
        const attrsKeys = newVal?.attrs;
        if (attrsKeys) {
          Object.keys(attrsKeys).forEach((key) => {
            if (
              STYLES.includes(key) &&
              (!vzAttributes.value[key] || vzAttributes.value[key] === "")
            ) {
              vzAttributes.value[key] = `${attrsKeys[key]}`;
            }
          });
        }
      },
      { deep: true }
    );
    //selectControl
    watch(getSelectedComponent, (newVal) => {
      if (newVal) {
        sideBarOption.value = "SETTINGS";
      } else if (sideBarOption.value === "SETTINGS") {
        sideBarOption.value = "CONTROLS";
      }
    });
    watch(
      selectControlColumn,
      (newVal) => {
        menuItem.value = [
          {
            label: "Row",
            icon: "las la-database fs-4 mt-1",
            items: [
              {
                label: "Add Row Below",
                icon: "pi pi-fw pi-plus",
                command: (event) => {
                  addRowBelow();
                },
              },
              {
                label: "Add Row Above",
                icon: "pi pi-fw pi-plus",
                command: (event) => {
                  addRowAbove();
                },
              },
              {
                label: "Delete Row",
                icon: "pi pi-fw pi-trash",
                command: () => {
                  deleteRow();
                },
              },
              {
                label: "Select Row",
                icon: "bi bi-check2-square",
                command: () => {
                  selectRow();
                },
              },
              {
                label: "Merge Rows",
                icon: "bi bi-intersect",
                command: () => {
                  mergeRows();
                },
              },
            ],
          },
          {
            label: "Column",
            icon: "las la-columns fs-4 mt-1",
            items: [
              {
                label: "Add Column",
                icon: "pi pi-fw pi-plus",
                command: (event) => {
                  addColumnLeft();
                },
              },
              {
                label: "Merge Columns",
                icon: "bi bi-intersect",
                command: (event) => {
                  mergeColumns();
                },
              },
              {
                label: "Delete Column",
                icon: "pi pi-fw pi-trash",
                command: (event) => {
                  deleteColumn();
                },
              },
            ],
          },
        ];
        if (newVal) {
          //Update menuItem with items
          menuItem.value.push({
            label: "Adjust Item",
            icon: "bi bi-text-center",
            items: [
              {
                label: "Start",
                icon: "bi bi-align-start",
                command: (event) => {
                  alignItems("start");
                },
              },
              {
                label: "End",
                icon: "bi bi-align-end",
                command: (event) => {
                  alignItems("end");
                },
              },
              {
                label: "Center",
                icon: "bi bi-align-center",
                command: () => {
                  alignItems("center");
                },
              },
              {
                label: "Between",
                icon: "bi bi-view-stacked",
                command: () => {
                  alignItems("between");
                },
              },
              {
                label: "Around",
                icon: "bi bi-distribute-horizontal",
                command: () => {
                  alignItems("around");
                },
              },
              {
                label: "Evenly",
                icon: "bi bi-window-dock",
                command: () => {
                  alignItems("evenly");
                },
              },
            ],
          });
          menuItem.value.push({
            label: "Select for Calculaions",
            icon: "las la-calculator",
            command: (event) => {
              selectForCalculation();
            },
          });
          menuItem.value.push({
            label: "Select for Results",
            icon: "las la-calculator",
            command: (event) => {
              applyCalculations();
            },
          });
        }
      },
      { immediate: true }
    );
    // formStructure
    watch(
      formStructure,
      async (...args) => {
        debouncedWatch.value(...args);
      },
      { deep: true }
    );

    function handleDragStart(e) {
      const path = e.path || (e.composedPath && e.composedPath());
      const id = findColumnIDFromPath(path);
      e.srcElement.style.opacity = "0.4";
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text", id);
    }

    function handleDragOver(e) {
      if (e.preventDefault) {
        e.preventDefault();
      }
      e.dataTransfer.dropEffect = "move";
      return false;
    }

    function handleDragEnter(e) {
      e.srcElement.classList.add("over");
    }

    function handleDragLeave(e) {
      e.srcElement.classList.remove("over");
    }

    async function handleDrop(e) {
      const path = e.path || (e.composedPath && e.composedPath());
      if (e.stopPropagation) {
        e.stopPropagation(); // stops the browser from redirecting.
      }
      const selectedId = e.dataTransfer.getData("text");
      const selectedDropped = droppableControls.value.find(
        (item) => item.id === selectedId || selectedId.indexOf(item.id) > -1
      );
      if (!selectedDropped) {
        const template = templateOptions.value.find(
          (item) => item.value === selectedId
        );
        if (template) {
          selectedSectionId.value = selectedId;
          showOverrideTableModal.value = true;
          return;
        }
      }
      if (selectedDropped) {
        drop(e, null);
        const items = document.querySelectorAll(".over");
        if (items) {
          items?.forEach(function (item) {
            item.classList.remove("over");
          });
        }
        return;
      }
      const droppedId = findColumnIDFromPath(path);
      //check if dropped on
      const rowIndex = findRowFromSelectedColumnId(selectedId);
      const columnIndex = findColumnFromSelectedColumnId(selectedId);

      const toMoveRowIndex = findRowFromSelectedColumnId(droppedId);
      const toMoveRowColumnIndex = findColumnFromSelectedColumnId(droppedId);

      if (
        rowIndex > -1 &&
        columnIndex > -1 &&
        toMoveRowIndex > -1 &&
        toMoveRowColumnIndex > -1
      ) {
        const elementIndex = formStructure.value[rowIndex].columns[
          columnIndex
        ]?.components?.findIndex(
          (component) => component?.attrs?.id === selectControl.value
        );
        if (elementIndex > -1) {
          const element = formStructure.value[rowIndex].columns[
            columnIndex
          ]?.components?.find(
            (component) => component?.attrs?.id === selectControl.value
          );
          formStructure.value[rowIndex].columns[columnIndex]?.components.splice(
            elementIndex,
            1
          );
          if (formStructure.value[rowIndex].columns[columnIndex].webId) {
            formStructure.value[rowIndex].columns[columnIndex].dirty = true;
          }
          if (isRowSavedOnServer?.value[rowIndex]) {
            formStructure.value[rowIndex].dirty = true;
          }
          //Push now
          if (element) {
            const dComponent = {
              ...element,
              webId: null,
              attrs: {
                ...element.attrs,
              },
            };
            formStructure.value[toMoveRowIndex].columns[
              toMoveRowColumnIndex
            ]?.components.push(dComponent);
            selectControlColumn.value = `${droppedId}`;
            if (isRowSavedOnServer?.value[toMoveRowIndex]) {
              formStructure.value[toMoveRowIndex].dirty = true;
              if (
                formStructure.value[toMoveRowIndex].columns[
                  toMoveRowColumnIndex
                ].webId
              ) {
                formStructure.value[toMoveRowIndex].columns[
                  toMoveRowColumnIndex
                ].dirty = true;
              }
            }
            commit();
            const items = document.querySelectorAll(".over");
            if (items) {
              items?.forEach(function (item) {
                item.classList.remove("over");
              });
            }
          }
        }
      }
      return false;
    }

    function handleDragEnd(e) {
      e.srcElement.style.opacity = "1";
      const items = document.querySelectorAll(".over");
      if (items) {
        items?.forEach(function (item) {
          item.classList.remove("over");
        });
      }
    }
    const setWebIDUUID = ({ key, webId }) => {
      if (webId && key) {
        if (webIdUUID.value.has(key)) {
          webIdUUID.value.delete(key);
        }
        webIdUUID.value.set(key, webId);
      }
    };
    const customEvent = (eventName) => {
      // find control and set attr image id
      const rowIndex = findRowFromSelectedColumnId(selectControlColumn.value);
      const columnIndex = findColumnFromSelectedColumnId(
        selectControlColumn.value
      );
      if (rowIndex > -1 && columnIndex > -1) {
        const elementIndex = formStructure.value[rowIndex].columns[
          columnIndex
        ]?.components?.findIndex(
          (component) => component?.attrs?.id === selectControl.value
        );
        if (
          elementIndex > -1 &&
          formStructure.value[rowIndex].columns[columnIndex].components[
            elementIndex
          ]?.attrs
        ) {
          formStructure.value[rowIndex].columns[columnIndex].components[
            elementIndex
          ].defaultValue = eventName.id;
          formStructure.value[rowIndex].columns[columnIndex].components[
            elementIndex
          ].attrs = {
            ...formStructure.value[rowIndex].columns[columnIndex].components[
              elementIndex
            ].attrs,
            imageId: eventName.id,
          };
          commit();
        }
      }
    };
    function handelTemplateDragStart(e) {
      const path = e.path || (e.composedPath && e.composedPath());
      const id =
        path?.find(
          (tag) =>
            tag.tagName === "LI" &&
            tag.id &&
            templateOptions.value.map((item) => item.value)?.includes(tag.id)
        )?.id ?? "";
      e.srcElement.style.opacity = "0.4";
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text", id);
    }
    function handleTemplateDragEnd(e) {
      e.srcElement.style.opacity = "1";
      const items = document.querySelectorAll(".over");
      if (items) {
        items?.forEach(function (item) {
          item.classList.remove("over");
        });
      }
    }
    async function confirmOverRide() {
      try {
        store.dispatch(Actions.ADD_BODY_CLASSNAME, "page-loading");
        const data = await SectionDesignAPI.getInstance().getFormDesign(
          selectedSectionId.value
        );
        const formData = transformToTableRow(data, sectionId.value);
        formStructure.value = [...formData];
        commit();
        handleRejectOverRide();
      } catch (e) {
        console.log(e);
      } finally {
        store.dispatch(Actions.REMOVE_BODY_CLASSNAME, "page-loading");
      }
    }
    function handleRejectOverRide() {
      selectedSectionId.value = undefined;
      showOverrideTableModal.value = false;
    }
    const showDialog = () => {
      showInitialDialog.value = true;
    };
    //watch on isMounted
    watch(isMounted, (newVal, oldVal) => {
      if (newVal && formStructure.value.length === 0) {
        showInitialDialog.value = true;
      }
    });
    watch(
      () => store.state.SITE_STORE.SELECTED_SITES,
      async (newValue, oldValue) => {
        // expandRoutedMenu();
        const addClass = document.getElementById("add") as HTMLElement;
        const addClas = document.getElementById("fields") as HTMLElement;
        const addCla = document.getElementById("text-tab") as HTMLElement;
        const addCl = document.getElementById(
          "TemplatesTemplates"
        ) as HTMLElement;
        if (addCl) {
          addCl.click();
        }
        if (addClass && addClas && addCla) {
          addClass.classList.add("show", "active");
          addClas.classList.add("show", "active");
          addCla.classList.add("show", "active");
        }
      }
    );

    return {
      showDialog,
      customEvent,
      handleDragStart,
      handleDragOver,
      handleDragEnter,
      handleDragLeave,
      handleDrop,
      handleDragEnd,
      controls,
      selectedControl,
      formStructure,
      masks,
      drag,
      dragElement,
      drop,
      allowDrop,
      addRowAbove,
      addRowBelow,
      deleteRow,
      addColumnLeft,
      mergeColumns,
      deleteColumn,
      selectColumn,
      menuItem,
      showContextMenu,
      menu,
      applyAttributes,
      vzAttributes,
      deleteSelectedItem,
      droppableControls,
      duplicateElement,
      columnIds,
      maxSpanInColumn,
      lastColumnColSpan,
      MAX_BLOCKS,
      selectControl,
      getSelectedComponent,
      undo,
      redo,
      history,
      canUndo,
      canRedo,
      createTable,
      selectedDropElement,
      createElement,
      publishForm,
      formSection,
      noOfColumns,
      filterdDroppableControls,
      searchTermDropableComponent,
      getFilteredControl,
      spanInColumn,
      filteredTemplateOptions,
      searchTermTemplate,
      getFilteredSection,
      selectControlColumn,
      isRowSavedOnServer,
      options,
      isTemplate,
      applyOperator,
      initTable,
      sideBarOption,
      openControlSetting,
      alignItems,
      selectedColumns,
      confirmDiscard,
      inProgressWebId,
      toggleAttr,
      isChecked,
      openDetailModal,
      selectedSection,
      toggleIsTemplate,
      loading,
      sectionId,
      mobilePreviewData,
      tabletPreviewData,
      getPreviewData,
      showModal,
      showDetailDialog,
      showInitialDialog,
      updatePreviewData,
      templateOptions,
      handelTemplateDragStart,
      handleTemplateDragEnd,
      toggleStyles,
      showOverrideTableModal,
      confirmOverRide,
      handleRejectOverRide,
      selectedTemplateId,
      selectedTemplateName,
      scrollRef,
      onClickTemplate,
      onClickControl,
      scrollControl,
    };
  },
});
